import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
	fetchAssetsRequest,
	fetchSettingsRequest,
} from '@Services/settings/settings.req';
import createThunkBody from '@Utils/createThunkBody';
import { SettingsState } from './settingsSlice.types';

/* side effects */
export const fetchSettings = createAsyncThunk<
	SettingsState,
	undefined,
	{ rejectValue: string }
>(
	'settings/fetch',
	async (_, thunkApi) =>
		await createThunkBody<SettingsState>(
			fetchSettingsRequest(),
			thunkApi,
			'Error fetching settings'
		)
);

export const fetchAssets = createAsyncThunk<
	ApiResponse<Record<string, string>>,
	undefined,
	{ rejectValue: string }
>(
	'settings/assets',
	async (_, thunkApi) =>
		await createThunkBody<ApiResponse<Record<string, string>>>(
			fetchAssetsRequest(),
			thunkApi,
			'Error fetching assets'
		)
);

const initialState: SettingsState = {
	fawry: true,
	aman: true,
	masary: true,
	credit_card: true,
	vf: true,
	online_cl: true,
	parent_portal: true,
	revision_section: true,
	exams_section: true,
	progress: true,
	classrooms_enabled: true,
	standalone_courses_enabled: false,
	bundles_enabled: false,
	whatsapp_enabled: false,
	sms_enabled: true,
	drawerOpened: false,
	assetsLoading: false,
	assetsResolved: false,
	assets: {},
};

export const toggleAppDrawer = createAction<boolean, 'toggleDrawer'>(
	'toggleDrawer'
);

const settingsSlice = createSlice({
	name: 'settings',
	initialState: initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchSettings.fulfilled, (state, { payload }) => {
			const data = ((payload as any)?.data ?? payload) as Partial<SettingsState>;
			state.classrooms_enabled = data?.classrooms_enabled ?? true;
			state.standalone_courses_enabled =
				data?.standalone_courses_enabled ?? false;
			state.bundles_enabled = data?.bundles_enabled ?? false;
			state.whatsapp_enabled = data?.whatsapp_enabled ?? false;
			// Default TRUE, unlike whatsapp: an older backend omits the key and
			// SMS was historically always on.
			state.sms_enabled = data?.sms_enabled ?? true;
			state.meta_title = data?.meta_title ?? null;
		});
		builder
			.addCase(fetchAssets.pending, (state) => {
				state.assetsLoading = true;
			})
			.addCase(fetchAssets.fulfilled, (state, { payload }) => {
				state.assets = payload.data;
				state.assetsLoading = false;
				state.assetsResolved = true;
			})
			.addCase(fetchAssets.rejected, (state) => {
				state.assetsLoading = false;
				// Resolved even on failure: there is no branded logo coming, so the
				// fallback is now the right thing to show rather than a blank space.
				state.assetsResolved = true;
			});
		builder.addCase(toggleAppDrawer, (state, payload) => {
			state.drawerOpened = payload.payload;
		});
	},
});

export default settingsSlice.reducer;
